/**
 * Thin client for api.wonders.salishforge.com. Cookies travel via fetch
 * credentials: 'include'; WebSocket auth is the same cookie since browsers
 * attach cookies on the upgrade request automatically.
 *
 * In dev, vite.config.ts proxies /api → :8787 so VITE_API_BASE_URL is
 * unset and `apiPath()` returns relative paths (same-origin from the
 * browser's view). In prod, VITE_API_BASE_URL points at the deployed
 * worker host and `apiPath()` returns absolute URLs so the browser hits
 * the right origin (and CORS lets the cookie flow back).
 */

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export const apiPath = (path: string): string => `${API_BASE}${path}`;

export const wsUrlFor = (path: string): string => {
  if (API_BASE) {
    return API_BASE.replace(/^http/, 'ws') + path;
  }
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}${path}`;
};

export type Me = {
  readonly playerId: string;
  readonly betaActive?: boolean;
  readonly betaExpiresAt?: number;
};

export type CreateMatchResponse = { readonly matchUlid: string };

export type StateResponse = {
  readonly ok: true;
  readonly publicState: unknown;
  readonly matchUlid: string;
};

/**
 * 401 from the worker means the session is gone. Server tells us where
 * the login route lives (defaults to /api/auth/login) so this client
 * stays decoupled from the auth provider.
 */
const f = async (path: string, init?: RequestInit): Promise<Response> => {
  const r = await fetch(apiPath(path), { ...init, credentials: 'include' });
  if (r.status === 401) {
    const body = (await r
      .clone()
      .json()
      .catch(() => null)) as { loginUrl?: string } | null;
    // Login URL is a path relative to the API host, not the SPA host.
    location.href = apiPath(body?.loginUrl ?? '/api/auth/login');
  }
  return r;
};

export const getMe = async (): Promise<Me> => {
  const r = await f('/api/me');
  // 403 with `beta_required` means the session is valid but the user has
  // not redeemed an invite yet. Surface as a Me with betaActive=false so
  // the SPA's existing `if (!m.betaActive) setNeedsRedeem(true)` branch
  // routes to the redemption page.
  if (r.status === 403) {
    const body = (await r.json().catch(() => null)) as { error?: string; playerId?: string } | null;
    if (body?.error === 'beta_required') {
      return { playerId: body.playerId ?? '', betaActive: false };
    }
  }
  if (!r.ok) throw new Error(`getMe: ${r.status}`);
  return (await r.json()) as Me;
};

export const redeemCode = async (
  code: string,
): Promise<{ ok: true; expiresAt: number } | { ok: false; error: string }> => {
  const r = await f('/api/auth/redeem-code', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => null)) as { error?: string } | null;
    return { ok: false, error: body?.error || `HTTP ${r.status}` };
  }
  return (await r.json()) as { ok: true; expiresAt: number };
};

export const createMatch = async (opponent?: string): Promise<CreateMatchResponse> => {
  const r = await f('/api/match', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(opponent ? { opponent } : {}),
  });
  if (!r.ok) throw new Error(`createMatch: ${r.status}`);
  return (await r.json()) as CreateMatchResponse;
};

export const getState = async (matchUlid: string): Promise<StateResponse> => {
  const r = await f(`/api/match/${matchUlid}/state`);
  if (!r.ok) throw new Error(`getState: ${r.status}`);
  return (await r.json()) as StateResponse;
};

export const submitMove = async (
  matchUlid: string,
  move: unknown,
): Promise<{ ok: boolean; seq?: number; error?: string }> => {
  const r = await f(`/api/match/${matchUlid}/move`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(move),
  });
  return (await r.json()) as { ok: boolean; seq?: number; error?: string };
};

export type WSMessage =
  | { kind: 'welcome'; viewer: string; publicState: unknown }
  | { kind: 'event_delta'; seq: number; events: readonly unknown[]; publicState: unknown }
  | { kind: 'move_rejected'; reason: string; hint?: string }
  | { kind: 'pong' }
  | { kind: 'error'; message: string }
  | { kind: 'narration_delta'; text: string }
  | { kind: 'narration_complete' };

export const openMatchSocket = (
  matchUlid: string,
  onMessage: (msg: WSMessage) => void,
  onClose?: () => void,
): WebSocket => {
  const url = wsUrlFor(`/api/match/${matchUlid}/ws`);
  const ws = new WebSocket(url);
  ws.addEventListener('message', (e) => {
    try {
      onMessage(JSON.parse(e.data) as WSMessage);
    } catch {
      // ignore malformed frames
    }
  });
  ws.addEventListener('close', () => {
    if (onClose) onClose();
  });
  return ws;
};
